import {Component, computed, inject, input, InputSignal} from '@angular/core';

import {AppConfigFacade, DimensionHelper} from '@selpia/app-config/data-access';
import {PerspectiveFacade} from '@selpia/perspective/data-access';
import {Breadcrumb, BreadcrumbsComponent, ScrollerComponent, SvgSpriteIcon} from '@selpia/shared/ui';
import {ArrayTools, Formatter} from '@selpia/shared/utils';
import {NavigationService} from '@selpia/data-batch/history-access';
import {Dimension, DimensionLevel, Perspective} from '@selpia/shared/models';

@Component({
  selector: 'lib-feature-tags',
  imports: [BreadcrumbsComponent, ScrollerComponent],
  templateUrl: './feature-tags.component.html',
  styleUrl: './feature-tags.component.scss',
})
export class FeatureTagsComponent {
  readonly #perspectiveFacade = inject(PerspectiveFacade);
  readonly #appConfigFacade = inject(AppConfigFacade);
  readonly #navigationService = inject(NavigationService);

  readonly withLevel: InputSignal<boolean> = input(true);

  protected readonly tags = computed(() => {
    const perspective = this.#perspectiveFacade.perspective();
    if (!perspective) {
      return [];
    }
    const formatString = this.#appConfigFacade.mainTagFormat();
    const allDimensions = this.#appConfigFacade.dimensions();

    const filterTags = FeatureTagsComponent.createTagsForFilters(perspective);

    if (!this.withLevel()) {
      return filterTags;
    }

    const levelTag = FeatureTagsComponent.createTagForDimensionLevel(perspective.focusDimensionLevel, formatString, allDimensions);

    return [levelTag, ...filterTags];
  });

  handleTagClick(tag: Breadcrumb): void {
    const codeToRemove = tag.code;
    const perspective = this.#perspectiveFacade.perspective();
    if (perspective) {
      this.#navigationService.updatePerspective(
        {
          filterDimensions: perspective.filterDimensions.filter(filter => filter.code !== codeToRemove),
        },
      );
    }
  }

  static createTagsForFilters(perspective: Perspective): Breadcrumb[] {
    return ArrayTools.removeFalsy(perspective.filterDimensions.map((filter) => {
      if (filter.parentCode) {
        return {...filter, closeable: true, icon: SvgSpriteIcon.Filter};
      }
      return undefined;
    }));
  }

  static createTagForDimensionLevel(dimensionLevel: DimensionLevel, formatString: string | undefined, allDimensions: Dimension[] | undefined): Breadcrumb {
    if (!formatString || !allDimensions) {
      return {
        code: dimensionLevel.name,
        name: dimensionLevel.name,
      };
    }

    const dimension = DimensionHelper.getDimensionOf(dimensionLevel, allDimensions);
    const data = {
      dimension: dimension.name,
      levelName: dimensionLevel.name,
    };

    const name = formatString ? Formatter.formatByString(formatString, data) : data.levelName;

    return {
      code: dimensionLevel.name,
      name,
    };
  }
}
